// HTTP status code constants for clarity and maintainability
const HTTP_STATUS = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  REQUEST_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
} as const;
// Error type constants for API Gateway and backend error mapping
const ERROR_TYPE_QUOTA_EXCEEDED = "QUOTA_EXCEEDED";
/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging";
import {
  IDEMPOTENT_HTTP_METHODS,
  RETRY_INTERVALS_MS,
  RETRY_POLICY
} from "../types";
import type { HttpMethod, TRetryOptions, RetryPolicy } from "../types";

const REQUEST_ABORTED_MESSAGE: string = "Request aborted";

/** Largest delay `setTimeout` accepts; anything above it fires immediately. */
const MAX_TIMER_DELAY_MS: number = 2147483647;

export class RetryMechanism {
  /**
  /**
   * Extracts the Retry-After delay from a response if present and valid.
   * Returns delay in ms, or null if not present/invalid.
   */
  private static getRetryAfterDelay(error: unknown): number | null {
    const response = RetryMechanism.extractResponse(error);
    if (response !== null && response !== undefined) {
      const headers = response.headers as Record<string, unknown> | undefined;
      if (headers !== null && headers !== undefined) {
        return RetryMechanism.parseRetryAfter(headers["retry-after"]);
      }
    }
    return null;
  }

  /**
   * Parses a Retry-After value into a delay in ms, or null when absent or
   * unusable.
   *
   * RFC 9110 allows two forms and the service schemas declare both
   * (`oneOf: [string, integer]`):
   * - delay-seconds — "15", or the number 15 when a transport hands the header
   *   over already parsed
   * - HTTP-date — "Wed, 21 Oct 2026 07:28:00 GMT", converted to the time
   *   remaining from now
   *
   * The server's value is honoured as-is — it takes precedence over every
   * policy interval, including when it is longer than the longest one. The
   * only limit is `setTimeout`'s own: a delay above 2^31-1 ms (~24.8 days)
   * overflows and fires immediately, so it is clamped to that maximum rather
   * than turning a long wait into an instant retry.
   */
  private static parseRetryAfter(value: unknown): number | null {
    let delayMs: number | null = null;
    if (typeof value === "number") {
      delayMs = value * 1000;
    } else if (typeof value === "string" && value.trim().length > 0) {
      const delaySeconds = Number(value);
      if (!Number.isNaN(delaySeconds)) {
        delayMs = delaySeconds * 1000;
      } else {
        const retryAt = Date.parse(value);
        if (!Number.isNaN(retryAt)) {
          delayMs = retryAt - Date.now();
        }
      }
    }
    if (delayMs === null || Number.isNaN(delayMs) || delayMs <= 0) {
      return null;
    }
    return delayMs > MAX_TIMER_DELAY_MS ? MAX_TIMER_DELAY_MS : delayMs;
  }
  /**
   * Returns true if the error is retryable by policy and error code.
   * Per UAN-155 §8 step 3: no-connectivity (null statusCode) is retryable.
   */
  private static shouldRetryError(
    statusCode: number | null,
    httpMethod: HttpMethod | undefined,
    hasRetryAfterHeader: boolean,
    errorType: string | undefined
  ): boolean {
    // No connectivity / timeout — no HTTP response received.
    // Per §8 step 3c: INTERACTIVE should retry with client-defined backoff.
    if (statusCode === null) {
      return true;
    }
    return RetryMechanism.isErrorRetryableBySpec(
      statusCode,
      httpMethod,
      hasRetryAfterHeader,
      errorType
    );
  }
  /**
   * Returns true if the retry policy is NEVER and should not retry.
   */
  private static isNeverPolicy(policy: RetryPolicy | undefined): boolean {
    return policy === RETRY_POLICY.NEVER;
  }
  /**
   * Returns true if INTERACTIVE policy and attempts exhausted.
   */
  private static isInteractiveExhausted(
    policy: RetryPolicy | undefined,
    attempt: number
  ): boolean {
    return (
      policy === RETRY_POLICY.INTERACTIVE &&
      attempt >= RETRY_INTERVALS_MS.INTERACTIVE.length
    );
  }
  private static readonly TAG: string = "RetryMechanism";
  /**
   * Cache of pending requests for deduplication.
   * Key is a serialised representation of the function + parameters.
   */
  private static readonly mPendingRequests: Map<string, Promise<unknown>> =
    new Map();
  /**
   * WeakMap to assign stable unique identifiers to functions for cache keys.
   * Prevents cache key collisions from functions with identical names.
   */
  private static readonly mFunctionIds: WeakMap<
    (...args: Array<unknown>) => Promise<unknown>,
    string
  > = new WeakMap();
  /**
   * Counter for generating unique function identifiers.
   */
  private static mNextFunctionId: number = 0;
  /**
   * Throws if the abort signal is set.
   */
  private static throwIfAborted(abortSignal?: AbortSignal): void {
    if (
      abortSignal !== null &&
      abortSignal !== undefined &&
      abortSignal.aborted
    ) {
      throw new Error(REQUEST_ABORTED_MESSAGE);
    }
  }
  /**
   * Handles retry error logic and returns delay if retryable, otherwise throws.
   */
  private static handleRetryError(
    error: unknown,
    policy: RetryPolicy | undefined,
    attempt: number,
    retryOptions: TRetryOptions,
    httpMethod: HttpMethod | undefined
  ): { delayMs: number } {
    if (RetryMechanism.isNeverPolicy(policy)) {
      Log.e(
        RetryMechanism.TAG,
        "executeWithRetry() - Policy NEVER: not retrying",
        error
      );
      throw error;
    }

    // Checked before the policy branches below, so it applies to CRITICAL too.
    // CRITICAL otherwise retries every error forever (UAN-155 §2.3/§4.3), which
    // for a definitive rejection means an unbounded loop behind a blocking
    // overlay. Opt-in: callers that pass no predicate are unaffected.
    if (
      retryOptions.isTerminal !== undefined &&
      retryOptions.isTerminal !== null &&
      retryOptions.isTerminal(error)
    ) {
      Log.e(
        RetryMechanism.TAG,
        "executeWithRetry() - Error is terminal: not retrying",
        error
      );
      throw error;
    }

    if (RetryMechanism.isInteractiveExhausted(policy, attempt)) {
      Log.e(
        RetryMechanism.TAG,
        `executeWithRetry() - INTERACTIVE: retries exhausted after ${attempt} attempts`,
        error
      );
      throw error;
    }

    // Infinite retry for CRITICAL after last interval (handled in delay logic)
    // Per UAN-155 §2.3 and §4.3: CRITICAL retries ALL errors regardless of
    // HTTP Error Code Specification retryability. Only extract Retry-After
    // for delay calculation, but never throw.

    const statusCode = RetryMechanism.extractStatusCode(error);
    // extractErrorType already returns `string | undefined`, so the previous
    // `?? undefined` was a no-op.
    const errorType = RetryMechanism.extractErrorType(error);
    // A server-supplied Retry-After always decides the wait, whatever the
    // policy: it outranks the INTERACTIVE and CRITICAL ladders, the silent
    // warm-up prefix, and the zero delay of a call with no policy. NEVER has
    // already thrown above, so it never waits at all.
    const retryAfterDelay: number | null =
      RetryMechanism.getRetryAfterDelay(error);
    const hasRetryAfterHeader: boolean = retryAfterDelay !== null;

    // CRITICAL bypasses the retryability check — always retries.
    if (
      policy !== RETRY_POLICY.CRITICAL &&
      !RetryMechanism.shouldRetryError(
        statusCode,
        httpMethod,
        hasRetryAfterHeader,
        errorType
      )
    ) {
      Log.e(
        RetryMechanism.TAG,
        `executeWithRetry() - Error is not retryable by policy (status ${statusCode})`,
        error
      );
      throw error;
    }

    let delayMs = 0;
    if (retryAfterDelay !== null && retryAfterDelay !== undefined) {
      delayMs = retryAfterDelay;
    } else if (
      policy === RETRY_POLICY.INTERACTIVE ||
      policy === RETRY_POLICY.CRITICAL
    ) {
      delayMs = RetryMechanism.getDelayForPolicyAttempt(
        retryOptions,
        attempt + 1
      );
    }
    return { delayMs };
  }

  /**
   * Get the delay for a specific retry attempt based on the policy.
   * For CRITICAL policy, repeats the last interval after exhausting the array.
   *
   * @param retryOptions - The retry configuration
   * @param attemptNumber - The current retry attempt number (1-based)
   * @returns The delay in milliseconds
   */
  private static getDelayForPolicyAttempt(
    retryOptions: TRetryOptions,
    attemptNumber: number
  ): number {
    // A quiet warm-up prefix retries silently before the policy ladder; after
    // it, the ladder runs from its start, so remap the attempt past the prefix.
    const warmup = retryOptions.initialRetryDelaysMs;
    let policyAttemptNumber = attemptNumber;
    if (warmup !== undefined && warmup !== null && warmup.length > 0) {
      if (attemptNumber <= warmup.length) {
        return warmup[attemptNumber - 1];
      }
      policyAttemptNumber = attemptNumber - warmup.length;
    }

    const policy = retryOptions.retryPolicy;

    if (policy === RETRY_POLICY.INTERACTIVE) {
      const intervals = RETRY_INTERVALS_MS.INTERACTIVE;
      const index = Math.min(policyAttemptNumber - 1, intervals.length - 1);
      return intervals[index];
    }

    if (policy === RETRY_POLICY.CRITICAL) {
      const intervals = RETRY_INTERVALS_MS.CRITICAL;
      const index = Math.min(policyAttemptNumber - 1, intervals.length - 1);
      return intervals[index];
    }

    // No policy - return 0 (no retry if not INTERACTIVE or CRITICAL)
    return 0;
  }

  /**
   * Check if an HTTP error is retryable based on the HTTP Error Code Specification.
   * Per UAN-155 §5.1 Error Code Table.
   *
   * @param statusCode - The HTTP status code
   * @param httpMethod - The HTTP method (for idempotency check)
   * @param hasRetryAfterHeader - Whether the response has a Retry-After header
   * @param errorType - Optional error type from response (e.g., "THROTTLED", "QUOTA_EXCEEDED")
   * @returns True if the error is retryable per the spec
   */
  private static isErrorRetryableBySpec(
    statusCode: number,
    httpMethod: HttpMethod | undefined,
    hasRetryAfterHeader: boolean,
    errorType?: string
  ): boolean {
    // Check idempotency for 502/504
    const isIdempotent =
      httpMethod !== undefined &&
      IDEMPOTENT_HTTP_METHODS.includes(
        httpMethod as (typeof IDEMPOTENT_HTTP_METHODS)[number]
      );

    switch (statusCode) {
      // 4XX errors - generally not retryable
      case HTTP_STATUS.BAD_REQUEST:
        // BAD_REQUEST - Only retry if Retry-After header present
        return hasRetryAfterHeader;

      case HTTP_STATUS.UNAUTHORIZED:
      case HTTP_STATUS.FORBIDDEN:
        // UNAUTHORIZED, ACCESS_DENIED - Not retryable
        return false;

      case HTTP_STATUS.NOT_FOUND:
        // RESOURCE_NOT_FOUND - Only retry if Retry-After header present
        return hasRetryAfterHeader;

      case HTTP_STATUS.CONFLICT:
      case HTTP_STATUS.REQUEST_TOO_LARGE:
      case HTTP_STATUS.UNSUPPORTED_MEDIA_TYPE:
        // CONFLICT, REQUEST_TOO_LARGE, UNSUPPORTED_MEDIA_TYPE - Not retryable
        return false;

      case HTTP_STATUS.TOO_MANY_REQUESTS:
        // THROTTLED is retryable, QUOTA_EXCEEDED is not (per spec)
        if (errorType === ERROR_TYPE_QUOTA_EXCEEDED) {
          return false;
        }
        return true;

      // 5XX errors - generally retryable
      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      case HTTP_STATUS.SERVICE_UNAVAILABLE:
        // Internal Server Error, Service Unavailable - Always retryable
        return true;

      case HTTP_STATUS.BAD_GATEWAY:
      case HTTP_STATUS.GATEWAY_TIMEOUT:
        // Bad Gateway, Gateway Timeout - Only retryable if idempotent
        return isIdempotent;

      default:
        // Other 5XX - retryable; other 4XX - not retryable
        if (statusCode >= 500 && statusCode < 600) {
          return true;
        }
        return false;
    }
  }

  /**
   * Extract the error type from an API Gateway error response.
   *
   * @param error - The error object
   * @returns The error type string, or undefined
   */
  private static extractErrorType(error: unknown): string | undefined {
    const response = RetryMechanism.extractResponse(error);

    if (response === null) {
      return undefined;
    }

    // Check x-amzn-errortype header (API Gateway)
    const headers = response.headers as Record<string, string> | undefined;
    if (headers !== null && headers !== undefined) {
      const errorType = headers["x-amzn-errortype"];
      if (typeof errorType === "string") {
        return errorType;
      }
    }

    // Check response body for error type
    const data = response.data as Record<string, unknown> | undefined;
    if (data !== null && data !== undefined) {
      const code = data.code;
      if (typeof code === "string") {
        return code;
      }
    }

    return undefined;
  }

  /**
   * Get or create a stable unique identifier for a function.
   * Prevents cache key collisions from functions with identical names.
   *
   * @param serviceFunction - The service function to identify
   * @returns A unique string identifier for this function instance
   */
  private static getFunctionId(
    serviceFunction: (...args: Array<unknown>) => Promise<unknown>
  ): string {
    const existingId = RetryMechanism.mFunctionIds.get(serviceFunction);

    if (existingId !== null && existingId !== undefined) {
      return existingId;
    }

    const newId = `fn_${RetryMechanism.mNextFunctionId++}`;
    RetryMechanism.mFunctionIds.set(serviceFunction, newId);
    return newId;
  }

  /**
   * Generate a cache key for deduplication from the service function
   * and its parameters. Uses a stable per-function identifier (via WeakMap)
   * to prevent collisions between functions with identical names or anonymous/bound functions.
   * This ensures that different service functions—even if they share the same name—
   * will not collide and incorrectly deduplicate requests.
   *
   * @param serviceFunction - The service function being called
   * @param serviceFunctionParameters - The parameters for the service function
   * @returns A string key uniquely identifying the request
   */
  private static generateCacheKey(
    serviceFunction: (...args: Array<unknown>) => Promise<unknown>,
    serviceFunctionParameters: Array<unknown>
  ): string {
    const functionId = RetryMechanism.getFunctionId(serviceFunction);
    const paramsKey = JSON.stringify(serviceFunctionParameters);
    return `${functionId}:${paramsKey}`;
  }

  /**
   * Safely extract the response object from an Axios-style error.
   *
   * @param error - The error object potentially containing a response
   * @returns The response record, or null if not available
   */
  private static extractResponse(
    error: unknown
  ): Record<string, unknown> | null {
    const errorRecord = error as Record<string, unknown>;

    if (errorRecord === null || errorRecord === undefined) {
      return null;
    }

    const response = errorRecord.response as Record<string, unknown>;

    if (response === null || response === undefined) {
      return null;
    }

    return response;
  }
  /**
   * Extract the HTTP status code from an error object.
   *
   * @param error - The error object with a response status
   * @returns The HTTP status code as a number, or null
   */
  private static extractStatusCode(error: unknown): number | null {
    const response = RetryMechanism.extractResponse(error);

    if (response === null) {
      return null;
    }

    const status = response.status;

    if (status === null || status === undefined) {
      return null;
    }

    const statusCode = Number(status);

    if (Number.isNaN(statusCode)) {
      return null;
    }

    return statusCode;
  }
  /**
   * Create a delay Promise that resolves after the specified ms.
   * Supports early cancellation via AbortSignal and an early, non-cancelling
   * wake via retryNowSignal.
   *
   * @param delayMs - The delay duration in milliseconds
   * @param abortSignal - Optional AbortSignal for cancellation
   * @param retryNowSignal - Optional signal that ends the wait early and
   *   resolves (so the loop re-attempts now) rather than rejecting
   * @returns A Promise that resolves, or rejects if aborted
   */
  private static wait(
    delayMs: number,
    abortSignal?: AbortSignal,
    retryNowSignal?: AbortSignal
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // NOSONAR - Optional chaining banned for SmartTV legacy compatibility
      if (
        abortSignal !== null &&
        abortSignal !== undefined &&
        abortSignal.aborted
      ) {
        reject(new Error(REQUEST_ABORTED_MESSAGE));
        return;
      }

      let onAbort: (() => void) | null = null;
      let onRetryNow: (() => void) | null = null;

      const cleanup = (): void => {
        if (
          abortSignal !== null &&
          abortSignal !== undefined &&
          onAbort !== null
        ) {
          abortSignal.removeEventListener("abort", onAbort);
        }
        if (
          retryNowSignal !== null &&
          retryNowSignal !== undefined &&
          onRetryNow !== null
        ) {
          retryNowSignal.removeEventListener("abort", onRetryNow);
        }
      };

      const timeoutId = setTimeout(() => {
        cleanup();
        resolve();
      }, delayMs);

      if (abortSignal !== null && abortSignal !== undefined) {
        onAbort = (): void => {
          clearTimeout(timeoutId);
          cleanup();
          reject(new Error(REQUEST_ABORTED_MESSAGE));
        };

        abortSignal.addEventListener("abort", onAbort, {
          once: true
        });
      }

      // A manual "retry now" collapses the remaining backoff and resolves, so
      // the loop re-attempts the same call immediately. Distinct from abort,
      // which rejects to tear the loop down.
      if (retryNowSignal !== null && retryNowSignal !== undefined) {
        if (retryNowSignal.aborted) {
          clearTimeout(timeoutId);
          cleanup();
          resolve();
          return;
        }
        onRetryNow = (): void => {
          clearTimeout(timeoutId);
          cleanup();
          resolve();
        };
        retryNowSignal.addEventListener("abort", onRetryNow, {
          once: true
        });
      }
    });
  }

  /**
   * Execute a service function with automatic retry on failure.
   *
   * Retries the provided service function based on the HTTP error
   * codes and strategies defined in the retryOptions. Supports abort
   * cancellation, request deduplication, and Retry-After headers.
   *
   * @typeParam ResponseType - The expected response type
   * @param serviceFunction - The API method (must be bound)
   * @param serviceFunctionParameters - Parameters array
   * @param retryOptions - Retry configuration
   * @param abortSignal - Optional AbortSignal to cancel retries
   * @param enableDeduplication - Deduplicate requests (default: false)
   * @returns A Promise resolving to the service response
   * @throws The original error if retries exhausted or not retryable
   */
  public static async callServiceWithRetry<ResponseType>(
    serviceFunction: (...args: Array<unknown>) => Promise<ResponseType>,
    serviceFunctionParameters: Array<unknown>,
    retryOptions: TRetryOptions,
    abortSignal?: AbortSignal,
    enableDeduplication: boolean = false
  ): Promise<ResponseType> {
    if (enableDeduplication) {
      const cacheKey = RetryMechanism.generateCacheKey(
        serviceFunction as (...args: Array<unknown>) => Promise<unknown>,
        serviceFunctionParameters
      );

      const pendingRequest = RetryMechanism.mPendingRequests.get(cacheKey);

      if (pendingRequest !== null && pendingRequest !== undefined) {
        Log.d(
          RetryMechanism.TAG,
          "callServiceWithRetry() - Returning deduplicated request"
        );
        return pendingRequest as Promise<ResponseType>;
      }

      const requestPromise = RetryMechanism.executeWithRetry<ResponseType>(
        serviceFunction,
        serviceFunctionParameters,
        retryOptions,
        abortSignal
      ).finally(() => {
        RetryMechanism.mPendingRequests.delete(cacheKey);
      });

      RetryMechanism.mPendingRequests.set(cacheKey, requestPromise);
      return requestPromise;
    }

    return RetryMechanism.executeWithRetry<ResponseType>(
      serviceFunction,
      serviceFunctionParameters,
      retryOptions,
      abortSignal
    );
  }

  /**
   * Internal retry execution loop.
   *
   * @typeParam ResponseType - The expected response type
   * @param serviceFunction - The API method to invoke
   * @param serviceFunctionParameters - Parameters array
   * @param retryOptions - Retry configuration
   * @param abortSignal - Optional AbortSignal to cancel retries
   * @returns A Promise resolving to the service response
   */
  private static async executeWithRetry<ResponseType>(
    serviceFunction: (...args: Array<unknown>) => Promise<ResponseType>,
    serviceFunctionParameters: Array<unknown>,
    retryOptions: TRetryOptions,
    abortSignal?: AbortSignal
  ): Promise<ResponseType> {
    const policy = retryOptions.retryPolicy;
    const httpMethod = retryOptions.httpMethod;
    let attempt = 0;

    while (true) {
      RetryMechanism.throwIfAborted(abortSignal);
      try {
        const result: ResponseType = await serviceFunction(
          ...serviceFunctionParameters
        );
        if (attempt > 0 && retryOptions.onSuccess !== undefined) {
          retryOptions.onSuccess();
        }
        return result;
      } catch (error: unknown) {
        const { delayMs } = RetryMechanism.handleRetryError(
          error,
          policy,
          attempt,
          retryOptions,
          httpMethod
        );
        if (retryOptions.onRetry !== undefined) {
          Log.d(
            RetryMechanism.TAG,
            "executeWithRetry() - Invoking onRetry callback"
          );
          retryOptions.onRetry(error, attempt, delayMs);
        }
        Log.i(
          RetryMechanism.TAG,
          `executeWithRetry() - Attempt ${attempt + 1} failed. Retrying in ${delayMs}ms (policy: ${policy})`
        );
        const retryNowSignal: AbortSignal | undefined =
          retryOptions.getRetryNowSignal !== undefined
            ? retryOptions.getRetryNowSignal()
            : undefined;
        await RetryMechanism.wait(delayMs, abortSignal, retryNowSignal);
        attempt++;
      }
    }
  }

  /**
   * Clear all pending deduplicated requests from the cache.
   * Should be called during component cleanup to prevent memory leaks.
   */
  public static clearPendingRequests(): void {
    Log.d(
      RetryMechanism.TAG,
      `clearPendingRequests() - Clearing ${RetryMechanism.mPendingRequests.size} pending requests`
    );
    RetryMechanism.mPendingRequests.clear();
  }
}
